// Pure projection of the producer's local crew state into the
// (toInsert, toUpdate, idsToDelete, crewIdMapping) tuple that the
// saveRelatedData side effects in CreateEditBriefing.tsx then push to
// Supabase.
//
// Extracted to give the partition logic dedicated regression coverage
// — the surrounding function is a 4000-line god component, and any
// refactor of the actual DB writes (parallelization, batching) needs
// the same partition decisions to come out of this step. If a change
// here breaks an assignment-id mapping or fails to flag a stale row
// for deletion, the unit suite catches it before staging.

export type LocalCrew = {
  id?: string;
  name: string;
  email: string;
  phone?: string | null;
  function: string;
  arrival_time?: string | null;
  event_date: string;
  color?: string | null;
};

export type ExistingCrew = {
  id: string;
  name: string;
  email: string;
  event_date: string;
};

export type CrewSavePayload = {
  briefing_id: string;
  name: string;
  email: string;
  phone: string | null;
  function: string;
  arrival_time: string | null;
  event_date: string;
  color: string | null;
  user_id?: string;
};

export type CrewSavePartition = {
  toUpdate: Array<CrewSavePayload & { id: string }>;
  toInsert: Array<CrewSavePayload & { _originalMemberId?: string }>;
  idsToDelete: string[];
  // local-id-or-key → db-id. Used downstream to translate
  // assignment.crew_member_id (which may be a local UUID) to the real
  // crew_members.id that survived the upsert.
  crewIdMapping: Map<string, string>;
  // The set of DB ids that the local state still references, so the
  // caller can compute idsToDelete = (all existing) \ usedDbIds.
  usedDbIds: Set<string>;
};

const matchKey = (m: { name: string; email: string; event_date: string }): string =>
  `${m.name.trim()}_${m.email.trim().toLowerCase()}_${m.event_date}`;

const blankToNull = (v: string | null | undefined): string | null =>
  v && v.trim() !== "" ? v : null;

export function partitionCrewSave(input: {
  briefingId: string;
  localCrew: LocalCrew[];
  existingCrew: ExistingCrew[];
  emailToUserId: Map<string, string>;
}): CrewSavePartition {
  const { briefingId, localCrew, existingCrew, emailToUserId } = input;

  const existingById = new Map<string, ExistingCrew>();
  const existingByKey = new Map<string, string>();
  for (const e of existingCrew) {
    existingById.set(e.id, e);
    existingByKey.set(matchKey(e), e.id);
  }

  const toUpdate: CrewSavePartition["toUpdate"] = [];
  const toInsert: CrewSavePartition["toInsert"] = [];
  const crewIdMapping = new Map<string, string>();
  const usedDbIds = new Set<string>();

  for (const member of localCrew) {
    const userId = emailToUserId.get(member.email.toLowerCase());
    const payload: CrewSavePayload = {
      briefing_id: briefingId,
      name: member.name,
      email: member.email.toLowerCase(),
      phone: blankToNull(member.phone),
      function: member.function,
      arrival_time: blankToNull(member.arrival_time),
      event_date: member.event_date,
      color: blankToNull(member.color),
      ...(userId ? { user_id: userId } : {}),
    };

    // PRIORITY 1: id match — preserves crew_assignments → crew_members
    // links across renames.
    if (member.id && existingById.has(member.id)) {
      toUpdate.push({ ...payload, id: member.id });
      crewIdMapping.set(member.id, member.id);
      usedDbIds.add(member.id);
      continue;
    }

    // PRIORITY 2: content key match (covers the case where the local
    // row lost its id, e.g. it was reconstructed from form state).
    const key = matchKey(member);
    const existingId = existingByKey.get(key);
    if (existingId && !usedDbIds.has(existingId)) {
      toUpdate.push({ ...payload, id: existingId });
      if (member.id) crewIdMapping.set(member.id, existingId);
      crewIdMapping.set(key, existingId);
      usedDbIds.add(existingId);
      continue;
    }

    // No match — genuinely new row. The caller patches crewIdMapping
    // after the DB insert returns the real id.
    toInsert.push({ ...payload, _originalMemberId: member.id });
  }

  const idsToDelete = existingCrew.map((e) => e.id).filter((id) => !usedDbIds.has(id));

  return { toUpdate, toInsert, idsToDelete, crewIdMapping, usedDbIds };
}
